import { useState, useEffect } from "react";
import { ArrowRight, Mail, MapPin, CalendarClock, Instagram, Facebook } from "lucide-react";

const KEY = "furn_hold_dismissed";

export const HoldPage = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(sessionStorage.getItem(KEY) !== "1");
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = show ? "hidden" : "";
    return () => { document.documentElement.style.overflow = ""; };
  }, [show]);

  if (!show) return null;

  const enter = () => {
    sessionStorage.setItem(KEY, "1");
    setShow(false);
  };

  return (
    <div data-testid="hold-page" className="fixed inset-0 z-[200] flex flex-col overflow-y-auto bg-background">
      <img
        src="https://images.unsplash.com/photo-1633251136226-d8b4ca5456cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
        alt="Carp lake at dawn"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/75 to-background/95" />

      <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
        <img src="/furn-logo.png?v=2" alt="Furn Angling" className="h-16 w-auto animate-fade-up sm:h-20" />

        <div className="mt-8 flex items-center gap-2.5 animate-fade-up" style={{ animationDelay: "80ms" }}>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
          </span>
          <span className="overline">Specialist Fishing Tackle</span>
        </div>

        <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight animate-fade-up sm:text-6xl md:text-7xl" style={{ animationDelay: "140ms" }}>
          We're not<br /><span className="text-accent">open yet.</span>
        </h1>

        <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/85 animate-fade-up sm:text-lg" style={{ animationDelay: "200ms" }}>
          We're busy putting the finishing touches to Furn Angling — your new home for premium carp, coarse, predator and sea fishing tackle. We'll update the site the moment we open, so check back soon.
        </p>

        <div className="mt-7 inline-flex items-center gap-2.5 border border-accent/40 bg-accent/10 px-5 py-3 animate-fade-up" style={{ animationDelay: "260ms" }}>
          <CalendarClock className="h-5 w-5 text-accent" />
          <span className="font-display text-sm font-semibold uppercase tracking-widest sm:text-base">Opening mid November 2026</span>
        </div>

        <div className="mt-9 flex flex-col items-center gap-4 animate-fade-up sm:flex-row" style={{ animationDelay: "320ms" }}>
          <button
            onClick={enter}
            data-testid="hold-preview-btn"
            className="group inline-flex items-center gap-2 bg-accent px-8 py-4 text-xs font-bold uppercase tracking-widest text-accent-foreground transition-[filter] hover:brightness-110"
          >
            Preview the Shop
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <a href="mailto:sales@furnangling.co.uk" className="inline-flex items-center gap-2 border border-foreground/30 px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors hover:border-accent hover:text-accent">
            <Mail className="h-4 w-4" /> Contact Us
          </a>
        </div>

        <div className="mt-10 flex flex-col items-center gap-4 text-sm text-muted-foreground animate-fade-up sm:flex-row sm:gap-8" style={{ animationDelay: "380ms" }}>
          <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-accent" /> sales@furnangling.co.uk</span>
          <span className="flex items-center gap-2 text-center"><MapPin className="h-4 w-4 text-accent" /> Unit 10, Blackhall Industrial Park, South Woodham Ferrers, CM3 5UW</span>
        </div>

        <div className="mt-6 flex gap-3 animate-fade-up" style={{ animationDelay: "440ms" }}>
          {[Instagram, Facebook].map((Icon, i) => (
            <a key={i} href="#" aria-label="Social" className="grid h-10 w-10 place-items-center border border-border text-muted-foreground transition-colors hover:border-accent hover:text-accent">
              <Icon className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HoldPage;
